import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { selectUserInfo } from "../selectors/userSelectors";
import { resolveOnboardingDestination } from "../utils/onboardingDestination";

// Routes where the onboarding redirect must NOT fire:
//  - /login: unauthenticated screen, LoginScreen owns its own post-auth redirect.
const SKIP_PATHS = new Set(["/login"]);

// Hydration phases that mean "the bootstrap fetch attempt has terminated and
// the redirect decision can be trusted".
//
//   'success'         — fresh server data drove the answer.
//   'failed'          — fetch attempted but errored; we accept the stale-cache
//                       answer to avoid blocking the user indefinitely. The
//                       redirect target re-fetches via getUserDetails on mount
//                       where applicable, so a wrong-stale redirect
//                       self-corrects within one screen.
//   'unauthenticated' — no auth at boot; the !userInfo guard below short-
//                       circuits, so this is harmless to admit.
//
// 'idle' and 'pending' are deliberately NOT in this set — at those phases the
// data is too unreliable to redirect on. App.js Splash gate already prevents
// this hook from mounting before bootstrap.phase === 'ready' (which fires
// AFTER hydration leaves 'pending'), so admitting only terminal hydration is
// belt-and-suspenders against future restructure.
const PROFILE_HYDRATION_TERMINAL = new Set([
  "success",
  "failed",
  "unauthenticated",
]);

export function useOnboardingRedirect() {
  // selectUserInfo (legacy) remains the authoritative read for the redirect
  // decision in Phase 3 — selector migration is a Phase 5 concern. The
  // bootstrap and hydration guards added here are defense-in-depth: they
  // become load-bearing once Phase 5 swaps this read to selectProfile.
  const userInfo = useSelector(selectUserInfo);
  const bootstrapPhase = useSelector((state) => state.bootstrap?.phase);
  const profileHydration = useSelector((state) => state.profile?.hydration);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // App.js Splash gate already blocks RootLayout from mounting until
    // bootstrap.phase === 'ready'. This guard is defense-in-depth in case
    // RootLayout is ever mounted outside the Splash gate (test harness,
    // future routing restructure). No-op in current arrangement.
    if (bootstrapPhase !== "ready") return;

    // Wait for the hydration attempt to terminate. Pending/idle = data is
    // unknown; redirecting now risks bouncing the user out of a route they
    // were on when bootstrap started. Becomes load-bearing under Phase 5.
    if (!PROFILE_HYDRATION_TERMINAL.has(profileHydration)) return;

    if (!userInfo) return;
    if (SKIP_PATHS.has(location.pathname)) return;

    const destination = resolveOnboardingDestination(userInfo);
    if (!destination) return;
    // Already on the forced step — skip to avoid a self-navigate loop
    // (especially /profile/email-verification while unverified).
    if (location.pathname === destination) return;

    navigate(destination, { replace: true });
  }, [
    userInfo,
    bootstrapPhase,
    profileHydration,
    location.pathname,
    navigate,
  ]);
}
